import {
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';

import { routes } from './app.routes';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { NgxEchartsModule } from 'ngx-echarts';
import { KeycloakInitService } from './shared/auth-services/keycloak-init.service';
import { KeycloakService } from 'keycloak-angular';
import { authHeadersInterceptor } from './interceptors/auth-headers.interceptor';
import { requestPayloadInterceptor } from './interceptors/request-payload.interceptor';
import { PlatformService } from '@shared-lib/shared-services/platform.service.service';
import { ENVIRONMENT_CONFIG } from '@shared-lib/shared-services/environment-config.token';
import { environment } from '../environments/environment';

// Custom override: ChartRendererService uses custom bar-chart (pink) and pie-chart (70% radius)
import {
  ChartRendererService,
  CustomGlobalFilterService,
  CustomChartFilterHandlerService,
} from '@custom';
import { ChartRendererService as SharedChartRendererService } from '@shared-lib/shared-services/chart-renderer.service';
import { GlobalFilterService as SharedGlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { ChartFilterHandlerService as SharedChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';

const spricedDataUrl = String((environment as any).SPRICED_DATA_URL || '').trim() ||
  `${String(environment.URL || '').replace(/\/+$/, '')}/spriced-data`;

export class QuoteEyeDebugErrorHandler implements ErrorHandler {
  private static readonly DEBUG_KEY = 'QUOTE_EYE_LOOP_DEBUG_2026_08_17';
  private counter = 0;

  handleError(error: any): void {
    this.counter += 1;
    const serializedError = this.serializeError(error);
    console.error(`[${QuoteEyeDebugErrorHandler.DEBUG_KEY}] ANGULAR_ERROR_HANDLER #${this.counter}`, {
      atMs: Math.round(performance.now()),
      message: serializedError?.message,
      stack: serializedError?.stack,
      error: serializedError,
    });

    console.error('ERROR', error);
  }

  private serializeError(error: any): any {
    if (!error) {
      return error;
    }

    const originalError = error?.ngOriginalError || error?.rejection || error;
    return {
      name: originalError?.name ?? error?.name,
      message: originalError?.message ?? error?.message ?? String(originalError),
      status: originalError?.status ?? error?.status,
      statusText: originalError?.statusText ?? error?.statusText,
      url: originalError?.url ?? error?.url,
      stack: typeof originalError?.stack === 'string'
        ? originalError.stack.split('\n').slice(0, 18).join('\n')
        : typeof error?.stack === 'string'
          ? error.stack.split('\n').slice(0, 18).join('\n')
          : undefined,
      keys: originalError && typeof originalError === 'object'
        ? Object.keys(originalError).slice(0, 30)
        : [],
    };
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: ErrorHandler, useClass: QuoteEyeDebugErrorHandler },
    provideZoneChangeDetection({ eventCoalescing: true }),
    importProvidersFrom(
      NgxEchartsModule.forRoot({ echarts: () => import('echarts') }),
    ),
    provideRouter(routes),
    provideAnimations(),
    provideHttpClient(withInterceptors([requestPayloadInterceptor, authHeadersInterceptor])),

    // Keycloak initializer
    {
      provide: APP_INITIALIZER,
      useFactory: (KeycloakInitService: KeycloakInitService) => () =>
        KeycloakInitService.initKeycloak(),
      multi: true,
      deps: [KeycloakInitService],
    },

    // Platform API initializer
    // {
    //   provide: APP_INITIALIZER,
    //   useFactory: (platformService: PlatformService) => () => platformService.getDashboardConfigFromApi('oh', 'customer-overview').toPromise(),
    //   multi: true,
    //   deps: [PlatformService],
    // },

    KeycloakService,
    // PlatformService,

    // Custom ChartRendererService override (uses pink bar-chart, 70% radius pie-chart)
    { provide: SharedChartRendererService, useClass: ChartRendererService },
    { provide: SharedGlobalFilterService, useClass: CustomGlobalFilterService },
    {
      provide: SharedChartFilterHandlerService,
      useClass: CustomChartFilterHandlerService,
    },

    // Environment config for shared library components
    {
      provide: ENVIRONMENT_CONFIG,
      useValue: {
        COMPANY_LOGO_SRC: environment.COMPANY_LOGO_SRC,
        COMPANY_LOGO_TEXT: environment.COMPANY_LOGO_TEXT,
        HELP_URL: environment.HELP_URL,
        FEEDBACK_URL: environment.FEEDBACK_URL,
        API_URL: environment.URL,
        SPRICED_DATA_URL: spricedDataUrl,
        USE_ALT_SIDENAV: environment.USE_ALT_SIDENAV,
      },
    },
  ],
};
